import { useState, useEffect, useRef } from "react"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import { JobbinInputGrey } from "../../components/jobbinInputGrey"
import { DateRangePicker } from "../../components/dateRangePicker"
import { createEvent, updateEvent, republishEvent, getMyEvents, EventResponse, CreateEventRequest, presignUpload, uploadToR2, formatEventRange } from "../../services/event-service"
import { useCounties, useCitiesByCounty } from "../../hooks/location-hooks"
import ConfirmModal from "../../components/confirmModal"
import { PlacesAutocomplete } from "../../components/placesAutocomplete"

const SectionLabel = ({ text }: { text: string }) => (
    <div className="flex items-center gap-3">
        <span className="text-xs font-bold uppercase tracking-wider text-figmaBlack">{text}</span>
        <div className="h-px flex-1 bg-figmaLightGray" />
    </div>
)

const DESCRIPTION_MAX = 1000

const pad = (n: number) => String(n).padStart(2, "0")
const timeOf = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`
// combină data (din calendar) cu ora (HH:mm) și formatează ca dată-oră locală, fără conversie UTC
const toLocalDateTime = (date: Date, time: string) => {
    const [h, m] = time.split(":").map(Number)
    const d = new Date(date)
    d.setHours(h || 0, m || 0, 0, 0)
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`
}

export const EventFormPage = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { id } = useParams<{ id: string }>()
    const isEdit = !!id

    // de unde a venit utilizatorul (listă sau pagina evenimentului) — ca să-l ducem înapoi acolo
    const navState = location.state as { from?: string; republish?: boolean } | null
    const from = navState?.from ?? "/events"
    const cameFromEvent = from !== "/events" && from.startsWith("/events/")
    const isRepublish = !!navState?.republish && isEdit

    const [isLoading, setIsLoading] = useState(true)
    const [notFound, setNotFound] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showConfirm, setShowConfirm] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const [title, setTitle] = useState("")
    const [description, setDescription] = useState("")
    const [address, setAddress] = useState("")
    const [selectedCountyCode, setSelectedCountyCode] = useState<string>("")
    const [cityId, setCityId] = useState<number | undefined>(undefined)
    const [dateFrom, setDateFrom] = useState<Date | null>(null)
    const [dateTo, setDateTo] = useState<Date | null>(null)
    const [originalDateFrom, setOriginalDateFrom] = useState<Date | null>(null)
    const [timeFrom, setTimeFrom] = useState("09:00")
    const [timeTo, setTimeTo] = useState("18:00")
    const [imageFile, setImageFile] = useState<File | null>(null)
    const [imagePreview, setImagePreview] = useState<string | null>(null)
    const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(null)
    const [dragOver, setDragOver] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        loadData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const loadData = async () => {
        setIsLoading(true)
        try {
            const eventsData = isEdit ? await getMyEvents() : []

            if (isEdit) {
                const ev = eventsData.find(e => e.id === id) ?? null
                if (!ev) {
                    setNotFound(true)
                } else {
                    setTitle(ev.title)
                    setDescription(ev.description ?? "")
                    setAddress(ev.address ?? "")
                    const evFrom = ev.dateFrom ? new Date(ev.dateFrom) : null
                    const evTo = ev.dateTo ? new Date(ev.dateTo) : null
                    setDateFrom(evFrom)
                    setDateTo(evTo)
                    setOriginalDateFrom(evFrom)
                    if (evFrom) setTimeFrom(timeOf(evFrom))
                    if (evTo) setTimeTo(timeOf(evTo))
                    setImagePreview(ev.imageUrl ?? null)
                    setOriginalImageUrl(ev.imageUrl ?? null)
                    // Evenimentul aduce deja codul de județ, deci selectăm cascada direct,
                    // fără să mai descărcăm toate localitățile ca să găsim orașul după id.
                    if (ev.countyCode) {
                        setSelectedCountyCode(ev.countyCode)
                        if (ev.cityId != null) setCityId(ev.cityId)
                    }
                }
            }
        } catch {
            // ignore
        }
        setIsLoading(false)
    }

    const { counties } = useCounties()
    const { cities: citiesForCounty } = useCitiesByCounty(selectedCountyCode)

    const goBack = () => navigate(from)

    // la editarea simplă a unui eveniment deja început, păstrăm selectabilă data de început originală
    // (la republicare backend-ul cere o perioadă viitoare, deci minimul rămâne azi)
    const startOfToday = new Date()
    startOfToday.setHours(0, 0, 0, 0)
    const pickerMinDate = isEdit && !isRepublish && originalDateFrom && originalDateFrom < startOfToday
        ? originalDateFrom
        : startOfToday

    const setFile = (file: File) => {
        setImageFile(file)
        setImagePreview(URL.createObjectURL(file))
    }

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) setFile(file)
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        setDragOver(false)
        const file = e.dataTransfer.files?.[0]
        if (file && file.type.startsWith("image/")) setFile(file)
    }

    const handleRemoveImage = () => {
        setImageFile(null)
        setImagePreview(isEdit ? originalImageUrl : null)
        if (fileInputRef.current) fileInputRef.current.value = ""
    }

    const validate = (): boolean => {
        if (!title || !dateFrom || !dateTo) {
            setError("Titlu și perioadă sunt obligatorii.")
            return false
        }
        if (!isEdit && !imageFile) {
            setError("Imaginea evenimentului este obligatorie.")
            return false
        }
        if (description.length > DESCRIPTION_MAX) {
            setError(`Descrierea poate avea maximum ${DESCRIPTION_MAX} de caractere.`)
            return false
        }
        if (new Date(toLocalDateTime(dateTo, timeTo)) <= new Date(toLocalDateTime(dateFrom, timeFrom))) {
            setError("Data și ora de sfârșit trebuie să fie după cele de început.")
            return false
        }
        return true
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        setError(null)
        if (!validate()) return
        // publicarea și republicarea costă credite → cerem confirmare; editarea simplă nu
        if (isEdit && !isRepublish) {
            doSubmit()
        } else {
            setShowConfirm(true)
        }
    }

    const doSubmit = async () => {
        if (!dateFrom || !dateTo) return
        setShowConfirm(false)
        const startISO = toLocalDateTime(dateFrom, timeFrom)
        const endISO = toLocalDateTime(dateTo, timeTo)
        setIsSubmitting(true)
        setError(null)
        try {
            let finalImageUrl: string | undefined
            if (imageFile) {
                const ext = imageFile.name.split(".").pop() ?? "jpg"
                const { uploadUrl, publicUrl } = await presignUpload(ext)
                await uploadToR2(uploadUrl, imageFile)
                finalImageUrl = publicUrl
            }

            const body: Partial<CreateEventRequest> = {
                title,
                description: description || undefined,
                address: address || undefined,
                cityId,
                dateFrom: startISO,
                dateTo: endISO,
                ...(finalImageUrl ? { imageUrl: finalImageUrl } : {}),
            }

            if (isRepublish) {
                await republishEvent(id!, body)
                navigate(from, { state: { flash: "Evenimentul a fost republicat." } })
            } else if (isEdit) {
                await updateEvent(id!, body)
                navigate(from, { state: { flash: "Modificările au fost salvate." } })
            } else {
                await createEvent({ ...body, imageUrl: finalImageUrl! } as CreateEventRequest)
                navigate("/events", { state: { flash: "Evenimentul a fost creat cu succes." } })
            }
        } catch (e: any) {
            if (e?.code === "insufficient.credits") {
                navigate("/credits")
                return
            }
            setError(e?.message || "A apărut o eroare. Încearcă din nou.")
            setIsSubmitting(false)
        }
    }

    if (isLoading) {
        return (
            <div className="flex justify-center py-24">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-figmaPrimary border-t-transparent" />
            </div>
        )
    }

    if (notFound) {
        return (
            <div className="flex flex-col items-center justify-center py-24 text-figmaDarkGray">
                <p className="text-lg font-semibold">Evenimentul nu a fost găsit.</p>
                <button onClick={() => navigate("/events")} className="mt-4 text-figmaPrimary font-semibold text-sm">
                    ← Înapoi la evenimente
                </button>
            </div>
        )
    }

    return (
        <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
                <div className="mx-auto w-full max-w-2xl">
                    <button
                        type="button"
                        onClick={goBack}
                        className="mb-4 flex items-center gap-2 text-sm font-bold text-figmaBlack transition-opacity hover:opacity-70"
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                            <path d="m15 18-6-6 6-6" />
                        </svg>
                        {cameFromEvent ? "Înapoi la eveniment" : "Înapoi la evenimente"}
                    </button>

                    <div className="bg-white rounded-2xl border border-figmaLightGray shadow-sm overflow-hidden">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-3 border-b border-figmaLightGray p-5 sm:p-6">
                            <div className="flex items-center gap-3">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-figmaPrimary/10 text-figmaPrimary">
                                    {isEdit ? (
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                                            <path d="M12 20h9" />
                                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                                        </svg>
                                    ) : (
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                                            <path d="M12 5v14M5 12h14" />
                                        </svg>
                                    )}
                                </div>
                                <div>
                                    <h3 className="font-bold text-figmaBlack text-lg leading-tight">
                                        {isRepublish ? "Republică eveniment" : isEdit ? "Editează eveniment" : "Eveniment nou"}
                                    </h3>
                                    <p className="text-xs text-figmaDarkGray mt-0.5">
                                        {isRepublish ? "Setează o perioadă nouă pentru a-l reactiva" : isEdit ? "Actualizează detaliile evenimentului tău" : "Completează detaliile noului eveniment"}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Body */}
                        <form onSubmit={handleSubmit}>
                            <div className="p-5 sm:p-6 space-y-8">
                                {/* Detalii */}
                                <section>
                                    <SectionLabel text="Detalii" />
                                    <div className="mt-4 space-y-5">
                                        <div>
                                            <label className="text-figmaBlack jobbin-input-label text-start">Titlu *</label>
                                            <JobbinInputGrey
                                                name="title"
                                                type="text"
                                                placeholder="Ex: Târg de joburi Oradea"
                                                value={title}
                                                onChange={(e: any) => setTitle(e.target.value)}
                                                classNames="mt-3"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-figmaBlack jobbin-input-label text-start">Descriere</label>
                                            <textarea
                                                placeholder="Detalii despre eveniment..."
                                                value={description}
                                                onChange={e => setDescription(e.target.value.slice(0, DESCRIPTION_MAX))}
                                                maxLength={DESCRIPTION_MAX}
                                                className="h-[140px] w-full rounded-[12px] bg-figmaLightGray px-5 py-[14px] text-[16px] mt-3"
                                                style={{ resize: "none" }}
                                            />
                                            <p className={`mt-1.5 text-right text-xs ${description.length >= DESCRIPTION_MAX ? "text-figmaContrast" : "text-figmaDarkGray"}`}>
                                                {description.length}/{DESCRIPTION_MAX}
                                            </p>
                                        </div>
                                    </div>
                                </section>

                                {/* Locație */}
                                <section>
                                    <SectionLabel text="Locație" />
                                    <div className="mt-4 space-y-5">
                                        <div>
                                            <label className="text-figmaBlack jobbin-input-label text-start">Adresă</label>
                                            <PlacesAutocomplete
                                                value={address}
                                                placeholder="Caută adresa..."
                                                wrapperClassName="relative mt-3"
                                                inputClassName="w-full rounded-[12px] bg-figmaLightGray px-5 py-[14px] pr-10 text-[16px] text-figmaBlack focus:outline-none"
                                                onPlaceSelected={(addr) => setAddress(addr)}
                                                onClear={() => setAddress("")}
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-figmaBlack jobbin-input-label text-start">Județ</label>
                                                <div className="relative mt-3">
                                                    <select
                                                        value={selectedCountyCode}
                                                        onChange={e => { setSelectedCountyCode(e.target.value); setCityId(undefined) }}
                                                        className="w-full appearance-none rounded-[12px] bg-figmaLightGray pl-5 pr-10 py-[14px] text-[16px] text-figmaBlack cursor-pointer"
                                                    >
                                                        <option value="">Alege județul</option>
                                                        {counties.map(c => (
                                                            <option key={c.code} value={c.code}>{c.name}</option>
                                                        ))}
                                                    </select>
                                                    <Chevron />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-figmaBlack jobbin-input-label text-start">Oraș</label>
                                                <div className="relative mt-3">
                                                    <select
                                                        value={cityId ?? ""}
                                                        onChange={e => setCityId(e.target.value ? Number(e.target.value) : undefined)}
                                                        disabled={!selectedCountyCode}
                                                        className="w-full appearance-none rounded-[12px] bg-figmaLightGray pl-5 pr-10 py-[14px] text-[16px] text-figmaBlack cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <option value="">Alege orașul</option>
                                                        {citiesForCounty.map(c => (
                                                            <option key={c.id} value={c.id}>{c.name}</option>
                                                        ))}
                                                    </select>
                                                    <Chevron />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                {/* Perioadă */}
                                <section>
                                    <SectionLabel text="Perioadă" />
                                    <div className="mt-4">
                                        <DateRangePicker
                                            startDate={dateFrom}
                                            endDate={dateTo}
                                            onChange={(s, e) => { setDateFrom(s); setDateTo(e) }}
                                            minDate={pickerMinDate}
                                            label={null}
                                        />
                                    </div>
                                    <div className="mt-4 grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-figmaBlack jobbin-input-label text-start">Ora de început</label>
                                            <div className="relative mt-3">
                                                <ClockIcon />
                                                <input
                                                    type="time"
                                                    value={timeFrom}
                                                    onChange={e => setTimeFrom(e.target.value)}
                                                    className="w-full rounded-[12px] bg-figmaLightGray pl-11 pr-4 py-[14px] text-[16px] text-figmaBlack"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-figmaBlack jobbin-input-label text-start">Ora de sfârșit</label>
                                            <div className="relative mt-3">
                                                <ClockIcon />
                                                <input
                                                    type="time"
                                                    value={timeTo}
                                                    onChange={e => setTimeTo(e.target.value)}
                                                    className="w-full rounded-[12px] bg-figmaLightGray pl-11 pr-4 py-[14px] text-[16px] text-figmaBlack"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    {dateFrom && dateTo && (
                                        <p className="mt-3 text-xs text-figmaDarkGray">
                                            🗓️ {formatEventRange(toLocalDateTime(dateFrom, timeFrom), toLocalDateTime(dateTo, timeTo))}
                                        </p>
                                    )}
                                </section>

                                {/* Imagine */}
                                <section>
                                    <SectionLabel text="Imagine" />
                                    <p className="text-xs text-figmaDarkGray mt-2">
                                        {isEdit ? "Lasă neschimbat pentru a păstra imaginea curentă." : "Imaginea evenimentului este obligatorie."}
                                    </p>
                                    <div className="mt-3">
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={handleImageChange}
                                        />
                                        {imagePreview ? (
                                            <div className="relative overflow-hidden rounded-[16px]">
                                                <img src={imagePreview} alt="preview" className="w-full h-52 object-cover" />
                                                <div className="absolute inset-x-0 bottom-0 flex justify-end gap-2 bg-gradient-to-t from-black/50 to-transparent p-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => fileInputRef.current?.click()}
                                                        className="rounded-full bg-white/95 px-4 py-1.5 text-xs font-semibold text-figmaPrimary shadow backdrop-blur-sm transition-colors hover:bg-white"
                                                    >
                                                        Schimbă
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={handleRemoveImage}
                                                        className="rounded-full bg-white/95 px-4 py-1.5 text-xs font-semibold text-figmaContrast shadow backdrop-blur-sm transition-colors hover:bg-white"
                                                    >
                                                        Elimină
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => fileInputRef.current?.click()}
                                                onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                                                onDragLeave={() => setDragOver(false)}
                                                onDrop={handleDrop}
                                                className={`flex w-full flex-col items-center justify-center gap-2 rounded-[16px] border-2 border-dashed py-10 transition-colors ${
                                                    dragOver
                                                        ? "border-figmaPrimary bg-figmaPrimary/5"
                                                        : "border-figmaDarkBg text-figmaPrimary hover:border-figmaPrimary hover:bg-figmaLightGray"
                                                }`}
                                            >
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8 text-figmaPrimary">
                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                    <path d="M17 8l-5-5-5 5" />
                                                    <path d="M12 3v12" />
                                                </svg>
                                                <span className="text-sm font-semibold text-figmaBlack">
                                                    Trage o imagine aici sau <span className="text-figmaPrimary">apasă pentru a încărca</span>
                                                </span>
                                                <span className="text-xs text-figmaDarkGray">JPG, PNG sau WebP</span>
                                            </button>
                                        )}
                                    </div>
                                </section>

                                {error && (
                                    <div className="flex items-center gap-2 rounded-xl bg-figmaContrast/10 px-4 py-3 text-sm font-medium text-figmaContrast">
                                        <span>⚠️</span> {error}
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="sticky bottom-0 flex items-center gap-3 border-t border-figmaLightGray bg-white/95 px-5 py-4 backdrop-blur sm:px-6">
                                {(!isEdit || isRepublish) && (
                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-figmaLightGray px-3 py-1.5 text-xs font-semibold text-figmaDarkGray">
                                        💳 {isRepublish ? "20" : "40"} credite
                                    </span>
                                )}
                                <div className="ml-auto flex gap-3">
                                    <button
                                        type="button"
                                        onClick={goBack}
                                        disabled={isSubmitting}
                                        className="rounded-xl bg-figmaLightGray px-5 py-3 text-sm font-semibold text-figmaBlack transition-colors hover:bg-figmaDarkBg/30 disabled:opacity-50"
                                    >
                                        Anulează
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="flex items-center justify-center gap-2 rounded-xl bg-figmaPrimary px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 active:scale-[0.98] disabled:opacity-50"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                                Se încarcă...
                                            </>
                                        ) : isRepublish ? "Republică — 20 credite" : isEdit ? "Salvează modificările" : "Publică eveniment"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={showConfirm}
                tone="primary"
                title={isRepublish ? "Republici evenimentul?" : "Publici evenimentul?"}
                message={isRepublish
                    ? `Se vor deduce 20 de credite pentru republicarea evenimentului „${title}”.`
                    : `Se vor deduce 40 de credite pentru publicarea evenimentului „${title}”.`}
                confirmText={isRepublish ? "Republică — 20 credite" : "Publică — 40 credite"}
                loading={isSubmitting}
                onConfirm={doSubmit}
                onCancel={() => setShowConfirm(false)}
            />
        </div>
    )
}

const Chevron = () => (
    <svg
        viewBox="0 0 24 24" fill="none" stroke="#9F9F9F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2"
    >
        <path d="m6 9 6 6 6-6" />
    </svg>
)

const ClockIcon = () => (
    <svg
        viewBox="0 0 24 24" fill="none" stroke="#486af6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2"
    >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
    </svg>
)
